/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable prefer-template */
/* eslint-disable object-shorthand */
/* eslint-disable prefer-const */
import React, { createContext, useContext } from 'react';
import { useFormik as useFormikOriginal } from 'formik';
import _ from 'lodash';

import { Grid, ListItemText } from '@mui/material';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import axios from '../../../../utils/axios';
import Yup from '../../../../utils/yup.tsx';
import { isNumeric } from '../../../../utils/utils.tsx';
import { RoleType } from '../../../../utils/enums.tsx';

export const API = 'invoice';
export const LANGUAGE = 'invoice';
export const name = LANGUAGE + 'Slice';
export const pageRoles = [
  RoleType.SuperAdmin,
  RoleType.OrganizationAdmin,
  RoleType.Manager,
  RoleType.Accountant,
  RoleType.Worker,
];

const fieldNames = [
  'building_id',
  'unit_id',
  'invoice_number',
  'reference_number',
  'description',
  'currency',
  'payment_model',
  'issue_date',
  'deadline_date',
  'transaction_date',
  'month',
  'year',
  'issue_place',
  'payer_name',
  'payer_address',
  'payer_city',
  'bank_account',
  'subtotal',
  'tax',
  'discount_rate',
  'discount',
  'total',
  'status',
  'paid_at',
] as const;

export const getFields = (t: any, field: string) => {
  const fields = fieldNames.map((fieldName) => ({
    id: fieldName,
    name: fieldName,
    label: t(LANGUAGE + '.table.' + fieldName),
    placeholder: '',
  }));

  return fields.find((item) => item.id === field);
};

export const getFilterOptions = (t: any) => [
  {
    id: 'all',
    name: 'all',
    label: t('status.all'),
    placeholder: '',
  },
  { ...getFields(t, 'invoice_number') },
  { ...getFields(t, 'payer_name') },
  { ...getFields(t, 'status') },
  { ...getFields(t, 'month') },
  { ...getFields(t, 'year') },
];

export const formSchema = (t: any, _id: number | null = null) =>
  Yup.object().shape({
    building_id: Yup.number().nullable().required().label(getFields(t, 'building_id')?.label),
    unit_id: Yup.number().nullable().required().label(getFields(t, 'unit_id')?.label),
    invoice_number: Yup.string().required().max(255).label(getFields(t, 'invoice_number')?.label),
    reference_number: Yup.string().nullable().max(255).label(getFields(t, 'reference_number')?.label),
    description: Yup.string().nullable().label(getFields(t, 'description')?.label),
    currency: Yup.string().nullable().max(3).label(getFields(t, 'currency')?.label),
    payment_model: Yup.string().nullable().max(50).label(getFields(t, 'payment_model')?.label),
    issue_date: Yup.date().required().label(getFields(t, 'issue_date')?.label),
    deadline_date: Yup.date().required().label(getFields(t, 'deadline_date')?.label),
    transaction_date: Yup.date().required().label(getFields(t, 'transaction_date')?.label),
    month: Yup.number().integer().min(1).max(12).nullable().label(getFields(t, 'month')?.label),
    year: Yup.number().integer().min(2000).required().label(getFields(t, 'year')?.label),
    issue_place: Yup.string().nullable().max(255).label(getFields(t, 'issue_place')?.label),
    payer_name: Yup.string().required().max(255).label(getFields(t, 'payer_name')?.label),
    payer_address: Yup.string().nullable().max(255).label(getFields(t, 'payer_address')?.label),
    payer_city: Yup.string().nullable().max(255).label(getFields(t, 'payer_city')?.label),
    bank_account: Yup.string().nullable().max(255).label(getFields(t, 'bank_account')?.label),
    subtotal: Yup.number().min(0).nullable().label(getFields(t, 'subtotal')?.label),
    tax: Yup.number().min(0).nullable().label(getFields(t, 'tax')?.label),
    discount_rate: Yup.number().min(0).max(100).nullable().label(getFields(t, 'discount_rate')?.label),
    discount: Yup.number().min(0).nullable().label(getFields(t, 'discount')?.label),
    total: Yup.number().min(0).required().label(getFields(t, 'total')?.label),
    status: Yup.string().nullable().max(50).label(getFields(t, 'status')?.label),
    paid_at: Yup.date().nullable().label(getFields(t, 'paid_at')?.label),
  });

let formikContext: any = null;
export const formikInstance = { current: null as any };
export const FormikContext = createContext<any>(null);

export const useFormikContext = () => {
  formikContext = useContext(FormikContext);

  if (!formikContext) {
    throw new Error('useFormikContext must be used within a FormikProvider');
  }

  return formikContext;
};

export const useFormik = (props: any) => {
  const [submitted, setSubmitted] = React.useState(false);
  const formik = useFormikOriginal({ ...props, validateOnChange: submitted });
  formikInstance.current = formik;

  React.useEffect(() => {
    if (formik.submitCount > 0) {
      setSubmitted(true);
    }
  }, [formik.submitCount]);

  React.useEffect(() => {
    if (formik.submitCount <= 0 && props?.onChangeBeforeSubmit) {
      props.onChangeBeforeSubmit();
    }
  }, [formik.values]);

  const resetSubmitted = React.useCallback(() => setSubmitted(false), []);
  const setSubmittedManually = React.useCallback((value: boolean) => setSubmitted(value), []);

  return {
    ...formik,
    submitted,
    resetSubmitted,
    setSubmitted: setSubmittedManually,
  };
};

const toDateInputValue = (value: any) => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

const toDateTimeInputValue = (value: any) => {
  if (!value) return '';
  return String(value).replace(' ', 'T').slice(0, 16);
};

const nullableNumber = (value: any) => {
  if (value === '' || value === null || value === undefined) return null;
  return Number(value);
};

export interface initialValuesStruct {
  building_id: number | string;
  unit_id: number | string;
  invoice_number: string;
  reference_number: string;
  description: string;
  currency: string;
  payment_model: string;
  issue_date: string;
  deadline_date: string;
  transaction_date: string;
  month: number | string;
  year: number | string;
  issue_place: string;
  payer_name: string;
  payer_address: string;
  payer_city: string;
  bank_account: string;
  subtotal: number | string;
  tax: number | string;
  discount_rate: number | string;
  discount: number | string;
  total: number | string;
  status: string;
  paid_at: string;
  items: InvoiceItemFormValue[];
}

export const initialValues: initialValuesStruct = {
  building_id: '',
  unit_id: '',
  invoice_number: '',
  reference_number: '',
  description: '',
  currency: '',
  payment_model: '',
  issue_date: '',
  deadline_date: '',
  transaction_date: '',
  month: '',
  year: '',
  issue_place: '',
  payer_name: '',
  payer_address: '',
  payer_city: '',
  bank_account: '',
  subtotal: '',
  tax: '',
  discount_rate: '',
  discount: '',
  total: '',
  status: '',
  paid_at: '',

  items: [] as InvoiceItemFormValue[],
};

export const prepareForm = (values: any = null, defValues: any = null) => {
  const form = _.cloneDeep(values);
  const data = _.cloneDeep(defValues);

  if (data && form) {
    fieldNames.forEach((field) => {
      data[field] = form[field] ?? '';
    });

    data.issue_date = toDateInputValue(form.issue_date);
    data.deadline_date = toDateInputValue(form.deadline_date);
    data.transaction_date = toDateInputValue(form.transaction_date);
    data.paid_at = toDateTimeInputValue(form.paid_at);
  }

  return data;
};

export const prepareData = (values: any = null, id: number | null = null) => {
  if (!values) return {};

  const data: any = {
    building_id: nullableNumber(values.building_id),
    unit_id: nullableNumber(values.unit_id),
    invoice_number: values.invoice_number || '',
    reference_number: values.reference_number || null,
    description: values.description || null,
    currency: values.currency || '',
    payment_model: values.payment_model || null,
    issue_date: values.issue_date || null,
    deadline_date: values.deadline_date || null,
    transaction_date: values.transaction_date || null,
    month: nullableNumber(values.month),
    year: nullableNumber(values.year),
    issue_place: values.issue_place || null,
    payer_name: values.payer_name || '',
    payer_address: values.payer_address || null,
    payer_city: values.payer_city || null,
    bank_account: values.bank_account || '',
    subtotal: nullableNumber(values.subtotal),
    tax: nullableNumber(values.tax),
    discount_rate: nullableNumber(values.discount_rate),
    discount: nullableNumber(values.discount),
    total: nullableNumber(values.total),
    status: values.status || '',
    paid_at: values.paid_at || null,
    items: values.items.map((item) => ({
      billable_service_id: item.billable_service_id,
      name: item.name,
      description: item.description,
      quantity: Number(item.quantity),
      price: Number(item.price),
      total: Number(item.quantity) * Number(item.price),
      billing_type: item.billing_type,
      unit_type: item.unit_type,
      fund_id: item.fund_id,
      start_date: item.start_date,
      end_date: item.end_date,
    })),
  };

  if (isNumeric(id)) {
    data.id = id;
  }

  return data;
};

export type InvoiceItemFormValue = {
  billable_service_id: number;
  name: string;
  description: string;
  quantity: number;
  price: number;
  total: number;
  billing_type?: string | null;
  unit_type?: string | null;
  fund_id?: number | null;
  start_date?: string | null;
  end_date?: string | null;
};

export const renderField = (
  label: string,
  value: any,
  options: { xs: number; sm: number; md: number } = { xs: 12, sm: 6, md: 4 }
) => (
  <Grid item xs={options.xs} sm={options.sm} md={options.md}>
    <ListItemText
      primary={value === null || value === undefined || value === '' ? '-' : value}
      secondary={label}
      primaryTypographyProps={{ typography: 'body2' }}
      secondaryTypographyProps={{
        component: 'span',
        typography: 'caption',
        color: 'text.disabled',
      }}
    />
  </Grid>
);

interface InitState {
  isLoading: boolean;
  show: boolean;
  id: number | null;
  payload: any;
  details: any;
  items: Array<any>;
}

function NewReducer() {
  const initialState: InitState = {
    isLoading: false,
    show: false,
    id: null,
    payload: null,
    details: null,
    items: [],
  };

  const reducers = {
    resetSlice: () => initialState,
    setLoading: (state: InitState, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setShow: (
      state: InitState,
      action: PayloadAction<{ show: boolean; id: number | null; payload: any }>
    ) => {
      state.id = action.payload.id;
      state.payload = action.payload.payload;
      state.show = action.payload.show;
    },
    startRead: (state: InitState) => {
      state.isLoading = true;
      state.items = [];
    },
    finishRead: (state: InitState, action: PayloadAction<any>) => {
      state.items = Array.isArray(action.payload)
        ? action.payload
        : action.payload?.data ?? [];
      state.isLoading = false;
    },
    startDetails: (state: InitState) => {
      state.isLoading = true;
    },
    finishDetails: (state: InitState, action: PayloadAction<any>) => {
      state.isLoading = false;
      state.details = action.payload;
    },
    start: (state: InitState) => {
      state.isLoading = true;
    },
    finish: (state: InitState, _action: PayloadAction<any>) => {
      state.isLoading = false;
    },
  };

  const { reducer, actions } = createSlice({
    name,
    initialState,
    reducers,
  });

  const getErrorMessage = (error: any) => error?.response?.data?.message || error?.message || '';

  const apis = {
    callReadApi:
      (params: any, callback: (state: boolean, data: any, message: string) => void) =>
      async (dispatch: any) => {
        dispatch(actions.startRead());

        try {
          const result = await axios.get(API, { params });
          const { data, message } = result.data;
          callback(true, data, message);
          dispatch(actions.finishRead(data));
        } catch (error: any) {
          const message = getErrorMessage(error);
          callback(false, null, message);
          dispatch(actions.finishRead(null));
        }
      },
    callDetailsApi:
      (id: number | null, callback: (state: boolean, data: any, message: string) => void) =>
      async (dispatch: any) => {
        dispatch(actions.startDetails());

        try {
          const result = await axios.get(API + '/' + id);
          const { data, message } = result.data;
          callback(true, data, message);
          dispatch(actions.finishDetails(data));
        } catch (error: any) {
          const message = getErrorMessage(error);
          callback(false, null, message);
          dispatch(actions.finishDetails(null));
        }
      },
    callCreateApi:
      (params: any, callback: (state: boolean, data: any, message: string) => void) =>
      async (dispatch: any) => {
        dispatch(actions.start());

        try {
          const result = await axios.post(API, params);
          const { data, message } = result.data;
          callback(true, data, message);
          dispatch(actions.finish(data));
        } catch (error: any) {
          const message = getErrorMessage(error);
          callback(false, null, message);
          dispatch(actions.finish(null));
        }
      },
    callUpdateApi:
      (params: any, callback: (state: boolean, data: any, message: string) => void) =>
      async (dispatch: any) => {
        dispatch(actions.start());

        try {
          const result = await axios.put(API + '/' + params?.id, params);
          const { data, message } = result.data;
          callback(true, data, message);
          dispatch(actions.finish(data));
        } catch (error: any) {
          const message = getErrorMessage(error);
          callback(false, null, message);
          dispatch(actions.finish(null));
        }
      },
    callDeleteApi:
      (params: any, callback: (state: boolean, data: any, message: string) => void) =>
      async (dispatch: any) => {
        dispatch(actions.start());

        try {
          const result = await axios.delete(API + '/' + params?.id, { data: params });
          const { data, message } = result.data;
          callback(true, data, message);
          dispatch(actions.finish(data));
        } catch (error: any) {
          const message = getErrorMessage(error);
          callback(false, null, message);
          dispatch(actions.finish(null));
        }
      },
  };

  return {
    reducer,
    ...actions,
    ...apis,
  };
}

export default NewReducer();
